import { useState } from 'react';
import { Lock, Unlock, Loader } from 'lucide-react';
import { cn } from '../utils/cn';

interface ConfidentialToggleProps {
  currentValue: boolean;
  onToggle: (newValue: boolean) => Promise<void>;
  disabled?: boolean;
  className?: string;
}

/**
 * A toggle button for marking evidence as confidential or removing the flag.
 * Calls the parent's onToggle callback which is expected to call PATCH /evidence/{id}.
 * Designed for the dark hover overlay on evidence thumbnails, so it keeps white
 * glyphs in both themes (`text-on-solid`).
 */
export const ConfidentialToggle = ({ currentValue, onToggle, disabled = false, className }: ConfidentialToggleProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading || disabled) return;

    setIsLoading(true);
    try {
      await onToggle(!currentValue);
    } catch {
      // Parent handles error display
    } finally {
      setIsLoading(false);
    }
  };

  const label = currentValue ? 'Remove confidential flag' : 'Mark as confidential';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={cn(
        'inline-flex items-center justify-center w-9 h-9 rounded-full text-on-solid transition-colors',
        currentValue
          ? 'bg-amber-500/70 hover:bg-amber-500/90'
          : 'bg-white/15 hover:bg-amber-500/60',
        (isLoading || disabled) && 'opacity-50 cursor-not-allowed',
        className,
      )}
      title={label}
      aria-label={label}
      aria-pressed={currentValue}
    >
      {isLoading ? (
        <Loader className="w-4 h-4 animate-spin" />
      ) : currentValue ? (
        <Lock className="w-4 h-4" />
      ) : (
        <Unlock className="w-4 h-4" />
      )}
    </button>
  );
};
